import { log2 } from "../../utils/log2";
import { getLastPrivateChat, setLastPrivateChat } from "../../utils/chat";
import { getPersistenceData, setPersistenceData } from "../../utils/persistenceStorage";
import { generateUUID } from "../../utils/session";

const log = log2("app.vuex");
const setting = {
    namespaced: true,
    state: {
        tabVisible: document.visibilityState === "visible",
        lastTabHiddenTS: null,
        multiSessionMode: false,
        privateChatIsOpen: false,
        privateChatIsMuted: getPersistenceData("privateChatIsMuted") === "true",
        chatBarCurrentPage: 1,
        chatBarPageSize: 9,
        fakeMode: false,
        shortcutReminder: [null, "true"].includes(getPersistenceData("shortcutReminder")),
        debugMode: false,
        headerPopoverStatus: {
            isHelpMenuPopoverOpen: false,
            isSettingPopoverOpen: false,
            isProfileMenuPopoverOpen: false,
            isRepresentativeSettingsPopoverOpen: false,
            notifications: false,
        },
        lastPrivateChat: getLastPrivateChat(),
        unreadPrivateChatCount: 0,
        beforeLastPrivateChat: getLastPrivateChat(),
        isPrivateChatPositionLast: false,
        largeHeader: true,
        liveSupportLoading: false,
        isNetConnectionStatusOnline: typeof navigator.onLine === "boolean" ? navigator.onLine : true,
        lastOfflineTimeStamp: (typeof navigator.onLine === "boolean" ? navigator.onLine : true) ? null : Date.now(),
        chatbotStoryViewActivity: [],
        visitorDetailPopup: null,
        userInteracted: false, //kullanıcı en az 1 click attıysa bu true olacak. ses ve video çalma işlemleri için kullanılacak
        windows: {},
        unreadNotificationCount: 0,
        reportCacheKey: parseInt(Math.random() * 100),
    },
    getters: {
        getPopoversStatus: (state) => {
            let pop = state.headerPopoverStatus;
            return (
                pop.isSettingPopoverOpen ||
                pop.isHelpMenuPopoverOpen ||
                pop.isProfileMenuPopoverOpen ||
                pop.isRepresentativeSettingsPopoverOpen
            );
        },
    },
    mutations: {
        SET_RANDOM_REPORT_CACHE_KEY: (state) => {
            state.reportCacheKey = parseInt(Math.random() * 100);
        },
        SET_NOTIFICATIONS_STATUS: (state, payload) => {
            state.headerPopoverStatus.notifications = payload;
        },
        SET_NET_CONNECTION_STATUS: (state, isOnline) => {
            if (!isOnline) state.lastOfflineTimeStamp = Date.now();
            state.isNetConnectionStatusOnline = isOnline;
        },
        SET_LIVE_SUPPORT_LOADING: (state, loading) => {
            log.debug("SET_LIVE_SUPPORT_LOADING", loading);
            state.liveSupportLoading = loading;
        },
        SET_CHAT_BAR_CURRENT_PAGE: (state, payload) => {
            log.debug("SET_CHAT_BAR_CURRENT_PAGE", payload);
            state.chatBarCurrentPage = payload;
        },
        SET_CHAT_FAKE_MODE: (state, payload) => {
            log.debug("SET_CHAT_FAKE_MODE", payload);
            state.fakeMode = payload;
        },
        SET_SHORTCUT_REMINDER_MODE: (state, payload) => {
            log.debug("SET_SHORTCUT_REMINDER_MODE", payload);
            setPersistenceData("shortcutReminder", payload);
            state.shortcutReminder = payload;
        },
        SET_DEBUG_MODE: (state) => {
            log.debug("SET_DEBUG_MODE");
            state.debugMode = true;
        },
        SET_CHAT_BAR_PAGE_SIZE: (state, payload) => {
            state.chatBarPageSize = payload;
        },
        SET_TAB_VISIBILITY_STATE: (state, payload) => {
            log.debug("SET_TAB_VISIBILITY_STATE", payload);
            state.tabVisible = payload;
            state.lastTabHiddenTS = Date.now();
        },
        SET_MULTI_SESSION_STATE: (state, payload) => {
            log.debug("SET_MULTI_SESSION_STATE", payload);
            state.multiSessionMode = payload;
        },
        TOGGLE_PRIVATE_CHAT_VISIBLE_STATE: (state) => {
            log.debug("TOGGLE_PRIVATE_CHAT_VISIBLE_STATE");
            state.privateChatIsOpen = !state.privateChatIsOpen;
        },
        TOGGLE_PRIVATE_CHAT_IS_MUTED_STATE: (state) => {
            state.privateChatIsMuted = !state.privateChatIsMuted;
            setPersistenceData("privateChatIsMuted", state.privateChatIsMuted);
        },
        SET_LAST_PRIVATE_CHAT: (state, payload) => {
            state.beforeLastPrivateChat = state.lastPrivateChat;
            state.lastPrivateChat = payload;
            setLastPrivateChat(payload);
        },
        SET_UNREAD_PRIVATE_CHAT_COUNT: (state, payload) => {
            state.unreadPrivateChatCount = payload;
        },
        REFRESH_UNREAD_PRIVATE_CHAT_COUNT: (state) => {
            state.unreadPrivateChatCount = 0;
        },
        SET_PRIVATE_CHAT_POSITION: (state, payload) => {
            state.isPrivateChatPositionLast = payload;
        },
        TOGGLE_LARGE_HEADER(state) {
            state.largeHeader = !state.largeHeader;
        },
        SET_LARGE_HEADER(state, value) {
            state.largeHeader = value;
        },
        SET_RESPANYIVATE_CHATBOT_ACTIVITY(state, value) {
            state.chatbotStoryViewActivity = value;
        },
        SET_VISITOR_DETAIL_POPUP(state, value) {
            state.visitorDetailPopup = value;
        },
        SET_USER_INTERACTED(state) {
            state.userInteracted = true;
        },
        SET_WINDOWS(state, val) {
            state.windows = val;
        },
        SET_NOTIFICATION_COUNT(state, val) {
            state.unreadNotificationCount = val;
        },
    },
    actions: {
        setRandomReportCacheKey({ commit }) {
            commit("SET_RANDOM_REPORT_CACHE_KEY");
        },
        setNotificationsStatus({ commit }, status) {
            commit("SET_NOTIFICATIONS_STATUS", status);
        },
        toggleLargeHeader({ commit, state }) {
            commit("TOGGLE_LARGE_HEADER");
        },
        setDebugMode({ commit }) {
            return commit("SET_DEBUG_MODE");
        },
        setChatFakeMode({ commit }, fakeMode) {
            return commit("SET_CHAT_FAKE_MODE", fakeMode);
        },
        setShortcutReminder({ commit }, shortcutReminder) {
            return commit("SET_SHORTCUT_REMINDER_MODE", shortcutReminder);
        },
        setChatBarCurrentPageChange({ commit }, page) {
            return commit("SET_CHAT_BAR_CURRENT_PAGE", page);
        },
        setChatBarPageSize({ commit, state }, pageSize) {
            if (state.chatBarPageSize !== pageSize) return commit("SET_CHAT_BAR_PAGE_SIZE", pageSize);
        },
        changeTabVisibilityChange({ commit }, payload) {
            return commit("SET_TAB_VISIBILITY_STATE", payload.visible);
        },
        changeMultiSessionMode({ commit }, payload) {
            return commit("SET_MULTI_SESSION_STATE", payload.isMultiSession);
        },
        togglePrivateChatVisibleState({ commit }) {
            return commit("TOGGLE_PRIVATE_CHAT_VISIBLE_STATE");
        },
        togglePrivateChatIsMutedState({ commit }) {
            return commit("TOGGLE_PRIVATE_CHAT_IS_MUTED_STATE");
        },
        setLastPrivateChat({ commit }, payload) {
            return commit("SET_LAST_PRIVATE_CHAT", payload);
        },
        setUnreadPrivateChatCount({ commit }, payload) {
            return commit("SET_UNREAD_PRIVATE_CHAT_COUNT", payload);
        },
        refreshUnreadPrivateChatCount({ commit }) {
            return commit("REFRESH_UNREAD_PRIVATE_CHAT_COUNT");
        },
        setIsPrivateChatPositionLast({ commit }, payload) {
            return commit("SET_PRIVATE_CHAT_POSITION", payload);
        },
        setLiveSupportLoading({ commit }, loading) {
            return commit("SET_LIVE_SUPPORT_LOADING", loading);
        },
        setchatbotStoryViewActivity({ commit }, payload) {
            return commit("SET_RESPANYIVATE_CHATBOT_ACTIVITY", payload);
        },
        setVisitorDetailPopup({ commit }, value) {
            return commit("SET_VISITOR_DETAIL_POPUP", value);
        },
        setUserInteracted({ commit }) {
            return commit("SET_USER_INTERACTED");
        },
        setWindows({ commit }, payload) {
            return commit("SET_WINDOWS", payload);
        },
        addNewWindow({ commit, state }, payload) {
            payload.id = generateUUID();
            const windows = {
                ...state.windows,
            };
            delete windows[payload.type];
            return commit("SET_WINDOWS", {
                ...windows,
                [payload.type]: payload,
            });
        },
        addNotificationCount({ commit, state }, val = 1) {
            return commit("SET_NOTIFICATION_COUNT", state.unreadNotificationCount + val);
        },
        subNotificationCount({ commit, state }, val = 1) {
            return commit("SET_NOTIFICATION_COUNT", state.unreadNotificationCount - val);
        },
        setNotificationCount({ commit }, val = 0) {
            return commit("SET_NOTIFICATION_COUNT", val);
        },
    },
};

export default setting;
